import type { SystemRoleKey } from "./schemas/role";
import type { TenantType, UsageArchetype } from "./schemas/tenant";

/**
 * Merkezî permission kataloğu (CLOS-83 `[026]`).
 *
 * Ürün genelindeki bütün yetkiler TEK yerde. Dağıtık tanımlanmış olsalardı "bu kullanıcı
 * neyi yapabiliyor?" sorusunun cevabı kod tabanını taramaktan geçerdi ve denetlenebilir
 * olmazdı.
 *
 * Katalog KOD, veri değil: aksiyonlar ürünün kendisiyle birlikte doğuyor ve derleme
 * zamanında bilinebiliyor. Veritabanına konsaydı, kodda var olmayan bir anahtar
 * atanabilir ya da kodda var olan bir anahtar katalogda hiç bulunmayabilirdi.
 * Rol→permission ATAMALARI ise tenant başına satır (DB); ikisi farklı şeyler.
 *
 * Biçim ve granülarite gerekçesi: `docs/adr/0021-permission-katalogu.md`.
 */

/** Katalog sürümü. Yeni anahtar eklendiğinde artar (kart: "versiyonlanabilir"). */
/**
 * ⚠ Katalog SÜRÜMÜ — her yeni yetki anahtarında ARTAR.
 *
 * `2`: `organization.lifecycle` eklendi (CLOS-195 `[054]`). Sürüm artmasaydı mevcut
 * kiracıların sistem rolleri hizalanmaz ve yeni yetki hiçbir organizasyonda kimseye
 * verilmezdi — özellik sessizce ölü doğardı (`tenants.permission_catalog_version`).
 *
 * `3`: `contract.terminate` eklendi (CLOS-529 [KARAR-01]).
 *
 * `5`: `client.workspace` eklendi (CLOS-175 `[122]`).
 *
 * `4`: `client.merge` (CLOS-171 `[118]`) ve `client.archive` (CLOS-172 `[119]`) eklendi.
 * Sürüm artmasaydı mevcut hukuk bürolarının sistem rolleri hizalanmaz ve iki yeni yetki
 * hiçbir organizasyonda kimseye verilmezdi — birleştirme ve arşivleme uçları herkese kapalı
 * kalır, özellik sessizce ölü doğardı.
 *
 * `6`: `organization.service` eklendi (CLOS-246 `[133]`). Asıl tüketicisi Müvekkil Alanı
 * (rolsüz tavan — hizalama gerektirmez) ama anahtar organizasyon domaininde olduğundan
 * büro/şirket org_admin'lerine de hizalamayla dağıtılır; o bağlamlarda bu anahtarı
 * kullanan uç yok.
 *
 * `7`: `organization.type_correction` eklendi (CLOS-812). Sürüm artmasaydı mevcut
 * organizasyonların yönetici rolü bu anahtarı almaz ve yanlış türle oluşmuş bir
 * organizasyonu HİÇ KİMSE düzeltemezdi — remediation'ın kurtarma yolu ölü doğardı.
 *
 * `8`: `client.contract_link` eklendi (CLOS-819) — `client.portfolio`'dan ayrıldı. Sürüm
 * artmasaydı mevcut hukuk bürolarının yönetici rolü yeni anahtarı almaz ve sözleşme
 * bağlama ucu HERKESE kapanırdı: mevcut bir yeteneği sessizce kaybettiren bir gerileme.
 *
 * `9`: kişisel çalışma alanının tavanına şablon anahtarları girdi (CLOS-825). Yeni bir
 * KATALOG anahtarı eklenmedi; değişen, `individual` türünün tavanı. Sürüm yine artıyor
 * çünkü kişisel alanların yetki görünümü değişti ve hizalama olmadan mevcut alanlar eski
 * tavanla kalırdı.
 *
 * `10`: `organization.audit` eklendi (CLOS-193 `[171]`). Audit ekranının kapısı ayrı bir
 * anahtar istiyor ve bugüne kadar hiçbiri bu anlamı taşımıyordu. Hizalama olmadan mevcut
 * organizasyonların yöneticileri yeni anahtarı hiç almaz ve ekran herkese kapalı kalırdı.
 *
 * `11`: `organization.audit_export` eklendi (CLOS-194 `[172]`). Kartın kriteri
 * görüntüleme ile dışa aktarmayı AYRI yetkiler sayıyor — biri diğerini içermiyor.
 *
 * `12`: kişisel çalışma alanının tavanına `subscription.view` girdi (CLOS-197 `[173]`).
 * Yeni bir KATALOG anahtarı eklenmedi; değişen, `individual` türünün tavanı — `9`'daki
 * şablon genişlemesiyle aynı sınıf. Sürüm yine artıyor çünkü hizalama olmadan mevcut
 * alanların yetki görünümü eski tavanla kalırdı.
 *
 * `13`: kişisel çalışma alanının tavanına `subscription.payment`, `subscription.invoice`
 * ve `subscription.entitlement` girdi (CLOS-202 `[176]`, CLOS-201 `[175]`). `12`'nin
 * bilerek KAPALI bıraktığı üç anahtar: o kartın (CLOS-197) kapsamı salt okunur bir
 * projeksiyondu ve *"yönetim ve ödeme CLOS-202/203'ün konusu"* diyordu. Kaynak şimdi VAR
 * — fatura profili, ödeme yöntemi ve ek hak satın alma CLOS-201/202 ile kişisel çalışma
 * alanı için de gerçek bir akış. Yeni bir KATALOG anahtarı eklenmedi (üçü de `subscription`
 * domaininde zaten Enterprise için tanımlıydı); değişen yine `individual` türünün tavanı.
 * `plan` ve `usage` KAPALI KALIYOR: plan yükseltme/düşürme CLOS-254/255'in, kullanım
 * sayaçları CLOS-198–200'ün konusu ve bu kart onları açmıyor.
 *
 * `14`: `contract.ai_analyze` eklendi (KARAR 07 — AI motoru ilk dilimi). Sürüm artmasaydı
 * mevcut organizasyonların sistem rolleri bu anahtarı hiç almaz ve AI analizi HERKESE
 * kapalı kalırdı: motor yazılır, kimse çalıştıramazdı. `organization.audit` (`10`) ile
 * birebir aynı sınıf — yeni bir ekranın kapısı, hizalama olmadan ölü doğar.
 *
 * `15`: `contract.export` eklendi (CLOS-234 `[198]`). Sürüm artmasaydı mevcut
 * organizasyonların yönetici rolü yeni anahtarı almaz ve sözleşme paketi dışa aktarma
 * ucu HERKESE kapalı kalırdı.
 *
 * `16`: `workflow.view` / `workflow.manage` / `workflow.publish` eklendi (Dinamik Workflow —
 * Kart 3). Sürüm artmasaydı mevcut organizasyonların yönetici rolü üç anahtarı da almaz ve
 * İş Akışları yüzeyi HERKESE kapalı doğardı; `contract.ai_analyze` (`14`) ile aynı sınıf.
 * ⚠ Standart kullanıcı hizalamayla yalnız `workflow.view` alır — dağıtım elle yazılan bir
 * listeden değil, risk sınıfından türüyor.
 */
export const PERMISSION_CATALOG_VERSION = 20;

/**
 * Risk sınıfları (kart: "standart/hassas/korunan").
 *
 * Sınıf dekoratif değil, YÜK TAŞIYOR: varsayılan rol setini ve verme sınırını bu belirliyor.
 */
export const RISK_CLASSES = ["standard", "sensitive", "protected"] as const;
export type RiskClass = (typeof RISK_CLASSES)[number];

export const RISK_CLASS_NAMES: Record<RiskClass, string> = {
  standard: "Standart",
  sensitive: "Hassas",
  protected: "Korunan",
};

/** Permission domainleri — kartın "başlangıç permission grupları" listesi. */
export const PERMISSION_DOMAINS = [
  "contract",
  "template",
  "client",
  "organization",
  "workflow",
  "ai",
  "subscription",
] as const;
export type PermissionDomain = (typeof PERMISSION_DOMAINS)[number];

export const PERMISSION_DOMAIN_NAMES: Record<PermissionDomain, string> = {
  contract: "Sözleşmeler",
  template: "Şablonlar",
  client: "Müvekkiller ve hizmet ilişkileri",
  organization: "Organizasyon yönetimi",
  workflow: "İş akışları",
  ai: "AI",
  subscription: "Abonelik",
};

export type PermissionEntry = {
  /** Kanonik anahtar: `<domain>.<action>`. Benzersiz ve DEĞİŞMEZ. */
  key: string;
  domain: PermissionDomain;
  risk: RiskClass;
  /** Kullanıcıya gösterilen açıklama (kart: "kullanıcıya gösterilen açıklamalar"). */
  description: string;
  /**
   * Önkoşul permission'lar. Eksikse atama reddedilir ve kullanıcıya açıklanır.
   *
   * Kural UYDURULMADI, yapısal: bir domaindeki her aksiyon o domainin TEMEL aksiyonuna
   * bağlı — göremediğin şeyi düzenleyemezsin. AI zincirinde ek bir bağ var: çıktıyı
   * uygulamak veya kaydetmek, önce onu görebilmeyi gerektirir.
   */
  requires: readonly string[];
};

/**
 * `GET /api/roles/catalog` yanıtındaki girdi (CLOS-83 `[026]`).
 *
 * `PermissionEntry`'ye ek olarak `grantableToCustomRole` taşıyor: verme sınırını SUNUCU
 * hesaplıyor. Arayüz risk sınıfından kendi kuralını türetseydi (*"protected ise gizle"*)
 * sunucudaki sınırla sessizce ayrışırdı — kart: *"UI kendi permission anlamını üretmez."*
 *
 * Tip burada, `@closer/shared`'da: aynı yanıtı hem üreten hem tüketen taraf okuyor ve iki
 * ayrı tanım gün gelip ayrışırdı (kriter 5).
 */
export type PermissionCatalogEntry = PermissionEntry & {
  /** Kart: *"korunan permission özel role atanamaz"* — sınır arayüze de taşınıyor. */
  grantableToCustomRole: boolean;
};

export type PermissionCatalog = { version: number; entries: PermissionCatalogEntry[] };

/**
 * Domain içi aksiyonlar. `view` her domainin temeli ve bağımlılık kökü.
 *
 * Aksiyon listeleri kartın "başlangıç permission grupları" bölümünden BİREBİR alındı;
 * granülarite aksiyon ailesi düzeyinde (ADR-0021). Daha ince bölmek (alan bazlı yetki)
 * kartsız bir ürün davranışı uydurmak olurdu.
 */
type Aksiyon = { action: string; risk: RiskClass; description: string };

const CONTRACT_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Sözleşmeleri görüntüleme" },
  { action: "create", risk: "standard", description: "Sözleşme oluşturma" },
  { action: "edit", risk: "standard", description: "Sözleşme düzenleme" },
  { action: "file", risk: "standard", description: "Sözleşme dosyalarını yönetme" },
  { action: "comment", risk: "standard", description: "Sözleşmeye yorum yazma" },
  { action: "revision", risk: "standard", description: "Sözleşme revizyonu oluşturma" },
  { action: "history", risk: "standard", description: "Sözleşme geçmişini görüntüleme" },
  { action: "review", risk: "standard", description: "Sözleşmeyi inceleme" },
  { action: "share", risk: "sensitive", description: "Sözleşmeyi paylaşma" },
  /**
   * SÖZLEŞME YÖNETİCİSİNİ DEĞİŞTİRME (CLOS-1212, §12.3).
   *
   * Kendi anahtarı var çünkü mevcut hiçbiri bu anlamı taşımıyor: `contract.edit` metni ve
   * alanları düzenler. Kanon İLK seçimi oluşturma bağlamına, SONRAKİ yeniden atamayı ayrı
   * bir yetkiye bağlıyor — sorumluyu değiştirmek, sözleşmeyi düzenlemekle aynı ağırlıkta
   * bir karar değil: sözleşmenin kime ait olduğunu değiştirir.
   *
   * `sensitive`: organizasyon içi sahiplik devri; geri alınabilir ama görünür sonucu var.
   */
  {
    action: "manager_assign",
    risk: "sensitive",
    description: "Sözleşme yöneticisini değiştirme",
  },
  { action: "approve", risk: "sensitive", description: "Sözleşmeyi onaylama" },
  { action: "reconcile", risk: "sensitive", description: "Sözleşme mutabakatını yürütme" },
  { action: "sign", risk: "sensitive", description: "Sözleşmeyi imzalama" },
  /**
   * ⚠ FESİH ayrı anahtar (CLOS-529 [KARAR-01]). `contract.edit` altında bırakmak, "metni
   * düzelt" yetkisi verilen bir kullanıcıya sözleşmeyi sonlandırma gücü de vermek demekti;
   * fesih geri alınamaz ticari bir sonuçtur, düzenleme değildir.
   */
  { action: "terminate", risk: "sensitive", description: "Sözleşmeyi feshetme" },
  { action: "archive", risk: "sensitive", description: "Sözleşmeyi arşivleme" },
  /**
   * ⚠ AI ANALİZİ SÖZLEŞME DOMAİNİNDE, `ai.*` ailesinde DEĞİL (KARAR 07/12).
   *
   * İlk bakışta `ai.use` bu işi karşılıyor gibi görünüyor ve iki sebeple karşılamıyor:
   *
   * 1. **Yetkinin nesnesi bir SÖZLEŞME.** Kapı iki soruyu birlikte soruyor: "AI
   *    çalıştırabilir misin?" ve "BU sözleşmeyi okuyabilir misin?". `contract.view`
   *    önkoşulu buradan geliyor ve `domainKatalogu` onu inşa gereği ekliyor —
   *    `ai.use`'un altına koysaydık o bağ ifade edilemezdi.
   * 2. **Kişisel çalışma alanı.** KARAR 12: *"Kişisel alanda AI: kredi hakkı 0 ise motor
   *    'hak yok' der."* Yani kişisel alanda AI'nin YÜZEYİ var, kapalı olan KOTA. `ai.*`
   *    ailesi `individual` türünün tavanında hiç yok; sözleşme domaini ise tamamen var
   *    (`permissionsForTenantType`). Anahtarı `ai.*` altına koysaydık kişisel alanda AI
   *    yüzeyi HİÇ açılmaz ve KARAR 12'nin cümlesi karşılıksız kalırdı — kota mesajı
   *    hiçbir zaman görünmezdi çünkü ekran hiç çizilmezdi.
   *
   * ⚠ `standard` sınıf: analiz bir OKUMA işi ve KARAR 11 çıktısını bir ÖNERİ sayıyor —
   * kendiliğinden versiyon, tarih ya da karar doğurmuyor. Geri alınamaz bir sonucu yok.
   * Krediyi harcaması ticari bir sonuç ama tavan zaten kredi hattında ve veritabanında;
   * yetkiyi hassas sınıfa çekmek, harcamayı iki yerden korumak olurdu.
   */
  { action: "ai_analyze", risk: "standard", description: "Sözleşmeyi AI ile analiz etme" },
  /**
   * SÖZLEŞME PAKETİNİ DIŞA AKTARMA (CLOS-234 `[198]`).
   *
   * ⚠ Kendi anahtarı var — kartın kriteri: *"export talebi ayrı bir permission'a tabidir."*
   * `contract.view`'ün altına saklansaydı, sözleşmeyi görebilen HERKES onu içeriğiyle
   * birlikte dışarı çıkarabilirdi; görüntülemek ile kalıcı bir kopya üretip taşımak aynı
   * risk sınıfında değil. `sensitive`: sonucu geri alınamaz (dosya bir kez indirildi mi
   * sistem dışına çıkmıştır) ve `contract.share`/`contract.sign` ile aynı sınıfta.
   */
  { action: "export", risk: "sensitive", description: "Sözleşme paketini dışa aktarma" },
];

const TEMPLATE_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Şablonları görüntüleme" },
  { action: "create", risk: "standard", description: "Şablon oluşturma" },
  { action: "edit", risk: "standard", description: "Şablon düzenleme" },
  { action: "use", risk: "standard", description: "Şablonu kullanma" },
  { action: "archive", risk: "sensitive", description: "Şablonu arşivleme" },
  { action: "manage", risk: "sensitive", description: "Şablon kütüphanesini yönetme" },
];

const CLIENT_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Müvekkilleri görüntüleme" },
  { action: "create", risk: "standard", description: "Müvekkil oluşturma" },
  { action: "edit", risk: "standard", description: "Müvekkil bilgilerini düzenleme" },
  /**
   * BİRLEŞTİRME kendi anahtarında (CLOS-171 `[118]`).
   *
   * ⚠ `client.edit`'in altına saklanamaz: düzenlemek bir alanı değiştirmek, birleştirmek iki
   * kaydı GERİ ALINAMAZ biçimde tek kayda indirmek. Aynı yetkiyle verilseydi, adres
   * düzeltmesi için yetki verilen herkes iki müvekkili birleştirebilirdi. `sensitive`:
   * sonucu geri alınamaz ve hukuki karışıklık üretebilir.
   */
  { action: "merge", risk: "sensitive", description: "Mükerrer müvekkilleri birleştirme" },
  /**
   * ARŞİVLEME kendi anahtarında (CLOS-172 `[119]`).
   *
   * ⚠ Kartın kriteri: *"aktörün arşivleme/yeniden aktifleştirme permission'ı ... doğrulanır."*
   * `client.edit`'in altına saklansaydı, telefon numarası düzeltmesi için yetki verilen
   * herkes bir müvekkili gündemden çıkarabilirdi. Şablon arşivlemesiyle (`template.archive`)
   * aynı ayrım.
   */
  {
    action: "archive",
    risk: "sensitive",
    description: "Müvekkil arşivleme ve yeniden aktifleştirme",
  },
  /**
   * SPONSORLU MÜVEKKİL ALANI kurma (CLOS-175 `[122]`).
   *
   * ⚠ Kendi anahtarı çünkü sonucu TİCARİ: büronun lisans hakkını tüketiyor ve müvekkile
   * ait ayrı bir çalışma alanı doğuruyor. `client.edit`'in altına saklansaydı, iletişim
   * bilgisi düzelten herkes büronun paketinden alan açabilirdi.
   */
  { action: "workspace", risk: "sensitive", description: "Sponsorlu Müvekkil Alanı kurma" },
  { action: "representative", risk: "sensitive", description: "Müvekkil temsilcisi yönetme" },
  { action: "portfolio", risk: "sensitive", description: "Müvekkil portföyünü yönetme" },
  /**
   * MÜVEKKİL–SÖZLEŞME BAĞI (CLOS-819).
   *
   * ⚠ `client.portfolio`'dan AYRILDI ve gerekçesi CLOS-813'te not düşülmüştü. O anahtar
   * büro İÇİ sorumluluk atamasını yönetiyor: hangi avukat hangi müvekkilden sorumlu.
   * Sözleşme bağlamak ise apayrı bir iş — "bu sözleşme bu müvekkile ait" demek — ve
   * bağımsız avukatın kişisel alanında atanacak bir ekip yokken de anlamlı.
   *
   * İkisi tek anahtarda kalsaydı iki kötü seçenek olurdu: ya bağımsız avukat sözleşme
   * bağlayamayacaktı (kartın 1. kabul kriteri), ya da ona büro içi portföy sorumluluğu
   * yetkisi verilecekti — kişisel alanda karşılığı olmayan bir yetenek.
   */
  {
    action: "contract_link",
    risk: "sensitive",
    description: "Müvekkili sözleşmeye bağlama",
  },
  { action: "service_scope", risk: "sensitive", description: "Hizmet kapsamını yönetme" },
];

const ORGANIZATION_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "Organizasyon bilgilerini görüntüleme" },
  { action: "profile", risk: "sensitive", description: "Organizasyon profilini düzenleme" },
  { action: "member", risk: "sensitive", description: "Kullanıcı ve üyelikleri yönetme" },
  { action: "invite", risk: "sensitive", description: "Organizasyona kullanıcı davet etme" },
  { action: "department", risk: "sensitive", description: "Departmanları yönetme" },
  /**
   * Güvenlik politikası (2FA zorunluluğu) — CLOS-292 `[046]`.
   *
   * Kendi anahtarı var çünkü mevcut hiçbiri bu anlamı taşımıyor: `organization.member`
   * üyelikleri, `organization.role` rolleri yönetir. Politikayı onlardan birinin altına
   * saklamak, "kim organizasyonun güvenlik kuralını değiştirebilir?" sorusunu başka bir
   * yetkinin gölgesinde bırakırdı. `protected`: geri alınması kolay olmayan ve bütün
   * üyeleri etkileyen bir karar.
   */
  { action: "security", risk: "protected", description: "Güvenlik politikasını yönetme" },
  { action: "role", risk: "protected", description: "Rolleri yönetme" },
  /**
   * Organizasyon YAŞAM DÖNGÜSÜ — kapatma ve yeniden etkinleştirme (CLOS-195 `[054]`).
   *
   * Kendi anahtarı var çünkü mevcut hiçbiri bu anlamı taşımıyor: `organization.profile`
   * bilgileri düzenler, `organization.security` politikayı yönetir. Kapatmayı onlardan
   * birinin altına saklamak, *"kim bütün organizasyonu durdurabilir?"* sorusunu başka bir
   * yetkinin gölgesinde bırakırdı. `protected`: bütün üyeleri etkileyen ve geri alınması
   * kolay olmayan bir karar.
   */
  { action: "lifecycle", risk: "protected", description: "Organizasyonu kapatma ve açma" },
  { action: "permission", risk: "protected", description: "Rol yetkilerini yönetme" },
  /**
   * YÖNETİŞİM ROLÜNÜ DEVRETME (CLOS-1207, Final Kanonik v1.0 §7.5).
   *
   * Kendi anahtarı var çünkü mevcut hiçbiri bu anlamı taşımıyor: `organization.role`
   * rolleri, `organization.permission` rol yetkilerini yönetir. Devri onlardan birinin
   * altına saklamak, *"kim organizasyonun yönetişimini devredebilir?"* sorusunu başka bir
   * yetkinin gölgesinde bırakırdı — kanon da devri AYRI, high-impact bir command olarak
   * tanımlıyor.
   *
   * `protected`: organizasyonun kimin elinde olduğunu değiştiren, geri alınması bir
   * başkasının iznine bağlı bir karar.
   */
  { action: "governance", risk: "protected", description: "Yönetişim rolünü devretme" },
  /**
   * AUDIT KAYITLARINI GÖRÜNTÜLEME (CLOS-193 `[171]`).
   *
   * Kendi anahtarı var çünkü mevcut hiçbiri bu anlamı taşımıyor: `organization.member`
   * üyelikleri, `organization.role` rolleri YÖNETİR; audit ise "kim ne yaptı" geçmişini
   * OKUMAKTIR. Yönetim yetkisinin altına saklamak, okuma hakkını yönetme hakkıyla
   * birleştirir ve *"kim meslektaşının bütün hareketlerini görebilir?"* sorusunu başka bir
   * yetkinin gölgesinde bırakırdı.
   *
   * `protected`: audit hukuki kanıttır ve bir çalışanın bütün hareketlerini bir başkasına
   * gösterebilir. Risk sınıfı burada yük taşıyor — `defaultPermissionsFor` yalnız
   * `standard` anahtarları standart kullanıcıya veriyor, yani bu anahtar varsayılan
   * olarak yalnız Organizasyon Yöneticisine düşüyor.
   *
   * ⚠ Bu anahtar OKUMA hakkıdır, HEDEF KAYDIN İÇERİĞİNİ görme hakkı DEĞİL (kartın kendi
   * kriteri). Kayıt erişimi ayrıca `record_access` defterinden soruluyor; audit yetkisi
   * onu baypas etmiyor.
   */
  { action: "audit", risk: "protected", description: "Audit kayıtlarını görüntüleme" },
  /**
   * AUDIT DIŞA AKTARMA (CLOS-194 `[172]`).
   *
   * ⚠ Görüntülemeden AYRI ve kartın kriteri bunu şart koşuyor: *"Audit görüntüleme ve
   * audit export ayrı permission'lardır; biri diğerini otomatik içermez."*
   *
   * Neden ayrı: ekranda okumak oturum içinde ve izlenebilir kalır; dışa aktarmak veriyi
   * ürünün DIŞINA çıkarır — bir dosya, bir posta kutusu, bir disk. Kanıt defterinin
   * kopyasını taşımak, onu okumaktan farklı bir sorumluluk.
   *
   * `protected`: dışarı çıkan veri geri çağrılamaz.
   */
  { action: "audit_export", risk: "protected", description: "Audit kayıtlarını dışa aktarma" },
  /**
   * HİZMET İLİŞKİSİ kararları — Müvekkil Alanı tarafı (CLOS-246 `[133]`).
   *
   * Kendi anahtarı var çünkü mevcut hiçbiri bu anlamı taşımıyor: `organization.view`
   * görüntüler, `organization.profile` bilgileri düzenler. Bir hukuk bürosuyla hizmet
   * ilişkisi başlatmak/onaylamak müvekkil adına verilen HUKUKİ bir karardır ve
   * `sensitive`'dir. Kaba kapı budur; ince kapı serviste: temsilci için `service_actions`
   * temsil kapsamı ayrıca doğrulanır (CLOS-178 adapter'ı). Büro tarafının anahtarı
   * `client.service_scope`'tur — iki taraf iki ayrı anahtarla yürür.
   */
  { action: "service", risk: "sensitive", description: "Hizmet ilişkisi taleplerini yönetme" },
  /**
   * ORGANİZASYON TÜRÜNÜN DÜZELTİLMESİ (CLOS-812).
   *
   * ⚠ Kendi anahtarı var ve bu kartın açık talebi: *"Organization Profile içinde sıradan
   * COMPANY <-> LAW_FIRM dropdown yok"*. `organization.profile`'ın altına saklansaydı,
   * adres ve telefon düzelten herkes organizasyonun TÜRÜNÜ değiştirebilirdi — türe bağlı
   * bütün yetenek kümesi (Müvekkiller, hizmet ilişkileri, rol seti) o kişinin eline
   * geçerdi.
   *
   * `protected`: tür bir kimlik kararıdır, alan boşken bile geri alınması gereken bir
   * hatadır ve ilişkili veri doğduktan sonra artık düzeltilemez.
   */
  {
    action: "type_correction",
    risk: "protected",
    description: "Organizasyon türünü düzeltme",
  },
];

/**
 * İŞ AKIŞI YÖNETİMİ (Dinamik Workflow — Kart 3).
 *
 * ### ⚠ Üç anahtar, üç ayrı risk sınıfı
 *
 * Sınıf burada da yük taşıyor: `defaultPermissionsFor` yalnız `standard` anahtarları
 * standart kullanıcıya veriyor. Üçü de aynı sınıfa konsaydı ya her üye iş akışı yayınlayabilir
 * ya da hiç kimse kendi organizasyonunun sürecini göremezdi.
 *
 * `view` `standard`: bir üyenin hangi süreçten geçtiğini görmesi, rolleri görmesi gibidir —
 * `organization.view` de aynı sınıfta ve Roller sayfasının kapısı odur.
 *
 * `manage` `sensitive`: departman yönetimiyle aynı ağırlık; taslağı değiştirmek çalışan
 * akışları etkilemez ama organizasyonun çalışma biçimini tanımlar.
 *
 * `publish` `protected`: yayınlanan sürüm GELECEKTEKİ bütün sözleşmelerin akışını belirler;
 * `organization.role` ile aynı sınıf. Taslağı düzenleyebilen ile yayınlayabileni ayırmak,
 * gözden geçirmesiz bir yayının kurumsal onay zincirini değiştirmesini engelliyor.
 *
 * ⚠ Bu anahtarlar workflow ATAMASI değildir: bir kullanıcıyı/rolü aşamaya atamak ona hiçbir
 * permission kazandırmaz (kanonik spesifikasyon §24).
 */
const WORKFLOW_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "standard", description: "İş akışlarını görüntüleme" },
  { action: "manage", risk: "sensitive", description: "İş akışı taslağı oluşturma ve düzenleme" },
  { action: "publish", risk: "protected", description: "İş akışı yayınlama ve varsayılan atama" },
  /**
   * ÇALIŞAN AKIŞA MÜDAHALE — remediation (CLOS-1215, Final Kanonik v1.0 §17.4).
   *
   * ⚠ Kendi anahtarı var ve kanon bunu AÇIKÇA istiyor: *"`workflow.instance.manage` ayrı
   * capability'dir."* `contract.approve` bir aşamayı TAMAMLAMA yetkisi; bu ise tıkanmış bir
   * aşamanın katılımcısını DEĞİŞTİRME yetkisi. Aynı anahtara bağlasaydık, onaylayan herkes
   * kendi görevini başkasına devredebilir ve akışın kimin elinde olduğu belirsizleşirdi.
   *
   * `protected`: çalışan bir sürecin katılımcısını değiştirmek, sürecin sonucunu
   * değiştirebilen bir müdahaledir.
   */
  {
    action: "instance_manage",
    risk: "protected",
    description: "Çalışan iş akışına müdahale (yeniden atama)",
  },
];

const AI_ACTIONS: Aksiyon[] = [
  { action: "use", risk: "standard", description: "AI işlevini kullanma" },
  { action: "output", risk: "standard", description: "AI çıktısını görüntüleme" },
  { action: "apply", risk: "standard", description: "AI çıktısını uygulama" },
  { action: "persist", risk: "sensitive", description: "AI çıktısını kalıcı kaydetme" },
];

const SUBSCRIPTION_ACTIONS: Aksiyon[] = [
  { action: "view", risk: "protected", description: "Abonelik bilgilerini görüntüleme" },
  { action: "plan", risk: "protected", description: "Abonelik planını yönetme" },
  { action: "usage", risk: "protected", description: "Abonelik kullanımını görüntüleme" },
  { action: "entitlement", risk: "protected", description: "Ek hakları yönetme" },
  { action: "payment", risk: "protected", description: "Ödeme işlemlerini yürütme" },
  { action: "invoice", risk: "protected", description: "Faturalama işlemlerini yürütme" },
];

/**
 * AI zincirindeki ek yapısal bağ.
 *
 * Temel aksiyon kuralı AI'da yetmiyor: çıktıyı UYGULAMAK ya da KAYDETMEK, önce onu
 * görebilmeyi gerektiriyor. Görmediği bir çıktıyı uygulayan kullanıcı, ne uyguladığını
 * bilmiyor demektir.
 */
const AI_EXTRA_REQUIREMENTS: Record<string, readonly string[]> = {
  "ai.apply": ["ai.output"],
  "ai.persist": ["ai.output"],
};

/**
 * `kokAksiyon`, domainin TEMEL aksiyonudur — diğer her aksiyon ona bağlanır.
 *
 * Sabit `view` varsayamıyoruz: AI domaininde görüntüleme diye bir aksiyon yok, temel
 * aksiyon `use`. İlk yazımda `view` sabitlenmişti ve katalog var olmayan bir `ai.view`
 * önkoşulunu üretiyordu — varsayılan setlerin bağımlılık tutarlılığını ölçen test yakaladı.
 */
function domainKatalogu(
  domain: PermissionDomain,
  aksiyonlar: Aksiyon[],
  kokAksiyon: string,
): PermissionEntry[] {
  const kok = `${domain}.${kokAksiyon}`;
  return aksiyonlar.map((a) => {
    const key = `${domain}.${a.action}`;
    const ekstra = AI_EXTRA_REQUIREMENTS[key] ?? [];
    const temel = key === kok ? [] : [kok];
    return {
      key,
      domain,
      risk: a.risk,
      description: a.description,
      requires: [...new Set([...temel, ...ekstra])].filter((k) => k !== key),
    };
  });
}

export const PERMISSION_CATALOG: readonly PermissionEntry[] = [
  ...domainKatalogu("contract", CONTRACT_ACTIONS, "view"),
  ...domainKatalogu("template", TEMPLATE_ACTIONS, "view"),
  ...domainKatalogu("client", CLIENT_ACTIONS, "view"),
  ...domainKatalogu("organization", ORGANIZATION_ACTIONS, "view"),
  ...domainKatalogu("workflow", WORKFLOW_ACTIONS, "view"),
  ...domainKatalogu("ai", AI_ACTIONS, "use"),
  ...domainKatalogu("subscription", SUBSCRIPTION_ACTIONS, "view"),
];

const KATALOG_INDEKS = new Map(PERMISSION_CATALOG.map((p) => [p.key, p]));

export const PERMISSION_KEYS: readonly string[] = PERMISSION_CATALOG.map((p) => p.key);

/**
 * SYSTEM GOVERNANCE CORE — yönetişim rolünün SÖKÜLEMEZ asgarisi (CLOS-1207, §7.6).
 *
 * ### ⚠ Neden var: tenant kendini kilitleyemesin
 *
 * Kanon çekirdeği açıkça *"tenant'ın kendisini kilitlemesini önleyecek minimum"* diye
 * tanımlıyor. Depoda risk somut: `protected` sınıfı yetkiler özel role atanamıyor
 * (`isGrantableToCustomRole`). Designation bir özel role devredilip çekirdek verilmeseydi,
 * o organizasyon rol/üyelik/yetki yönetimini yapamaz hâle gelirdi — kimse kimseye rol
 * atayamaz, kimse daveti yönetemez, kimse durumu düzeltemezdi.
 *
 * ### ⚠ Çekirdek DIŞI her şey dinamik
 *
 * Kanon: *"Business permission'lar bu core'un dışında tamamen dinamiktir."* Buraya yalnız
 * organizasyonun kendini yönetmeye devam etmesi için ZORUNLU olanlar giriyor. Liste
 * büyütülürse tenant'ın kendi yönetişim rolünü şekillendirme hakkı sessizce daralır.
 *
 * ⚠ `audit`/`audit_export` BİLEREK YOK: geçmişi okumak, organizasyonu yönetmeye devam
 * etmek için zorunlu değil. Denetim okuma hakkı tenant'ın kendi kararı olarak kalıyor.
 */
export const SYSTEM_GOVERNANCE_CORE: readonly string[] = [
  /** temel organization governance yönetimi */
  "organization.view",
  "organization.lifecycle",
  /** membership yönetimini sürdürebilme */
  "organization.member",
  "organization.invite",
  /** role/permission yönetimini sürdürebilme */
  "organization.role",
  "organization.permission",
  /** governance transferini yapabilme */
  "organization.governance",
  /** D06 enterprise governance erişimi */
  "subscription.view",
  "subscription.plan",
];

/** Bu yetki designation'dan TÜREYEN çekirdeğin parçası mı? */
export function isGovernanceCore(key: string): boolean {
  return SYSTEM_GOVERNANCE_CORE.includes(key);
}

/**
 * ASLA SAKLANMAYAN yetkiler — yalnız designation'dan türeyebilirler (CLOS-1209).
 *
 * ### ⚠ "Türetilir" ile "saklanamaz" AYNI ŞEY DEĞİL
 *
 * Çekirdeğin tamamı yönetişim rolüne çözümleme anında EKLENİYOR. Ama çoğu üyesi aynı
 * zamanda sıradan bir yetki: `organization.view` başka yetkilerin ÖNKOŞULU,
 * `organization.member` özel role meşru biçimde verilebiliyor. Onları saklanamaz ilan
 * etseydik önkoşul zinciri kopardı ve kullanıcı *"yetkim var ama göremiyorum"* durumuna
 * düşerdi — deponun kendi bağımlılık bekçisi bunu ölçüyor.
 *
 * Saklanması YASAK olan tek anahtar, yönetişimin devrini yapma yetkisidir: onun tek meşru
 * kaynağı designation'dır. Saklanabilseydi, yönetişimi devreden rol devretme gücünü
 * üzerinde tutar ve devri tek taraflı geri alabilirdi — canlıda ölçülen kusur buydu.
 */
export const NEVER_STORED_PERMISSIONS: readonly string[] = ["organization.governance"];

export function isNeverStored(key: string): boolean {
  return NEVER_STORED_PERMISSIONS.includes(key);
}

export function findPermission(key: string): PermissionEntry | undefined {
  return KATALOG_INDEKS.get(key);
}

export function isKnownPermission(key: string): boolean {
  return KATALOG_INDEKS.has(key);
}

/**
 * KORUNAN permission özel role atanamaz (kart kriteri).
 *
 * Verme sınırı burada tanımlanır, uygulama noktalarında değil: her yeni atama akışı kendi
 * kontrolünü yazsaydı biri unutulur ve o yoldan yetki yükseltme mümkün olurdu.
 */
export function isGrantableToCustomRole(key: string): boolean {
  return findPermission(key)?.risk !== "protected";
}

/**
 * Bir permission kümesindeki EKSİK önkoşullar.
 *
 * Boş dizi dönerse küme tutarlı. Dönen her anahtar, kullanıcıya "şunu da seçmelisiniz"
 * diye gösterilecek somut bir eksiktir — kart: *"eksik önkoşullar reddedilir ve kullanıcıya
 * açıklanır."*
 */
export function missingPrerequisites(keys: readonly string[]): string[] {
  const secilen = new Set(keys);
  const eksik = new Set<string>();
  for (const key of keys) {
    for (const onkosul of findPermission(key)?.requires ?? []) {
      if (!secilen.has(onkosul)) eksik.add(onkosul);
    }
  }
  return [...eksik].sort();
}

/**
 * Bir kümeyi BAĞIMLILIK OLARAK TUTARLI hâle budar (CLOS-87 `[030]`).
 *
 * Çoğaltmada kaynak rolün bazı yetkileri düşebiliyor (katalogdan çıkmış, korunan sınıfa
 * geçmiş ya da aktörde yok). Önkoşulu düşen bir yetkiyi öylece bırakmak, kaydedilemeyen
 * bir küme üretirdi — kullanıcıya *"kopyaladım ama kaydedemiyorum"* demek zorunda kalırdık.
 *
 * Döngü ŞART: bir yetkiyi çıkarmak, ona bağlı başka bir yetkiyi de geçersiz kılabilir.
 * Tek geçişte budamak zinciri yarım bırakırdı.
 */
export function pruneToConsistent(keys: readonly string[]): string[] {
  let kalan = [...new Set(keys)].filter(isKnownPermission);

  for (;;) {
    const kume = new Set(kalan);
    const sonraki = kalan.filter((key) =>
      (findPermission(key)?.requires ?? []).every((onkosul) => kume.has(onkosul)),
    );
    if (sonraki.length === kalan.length) return sonraki.sort();
    kalan = sonraki;
  }
}

/**
 * Çalışma alanı türünde KULLANILABİLİR permission'lar.
 *
 * Müvekkil ve hizmet ilişkisi domaini yalnız HUKUK BÜROSUNDA var: müvekkil hizmet
 * ilişkileri hukuk bürosunun bağlamıdır (CLOS-72 `[024]`). Şirkette de açık olsaydı,
 * hiçbir zaman doldurulmayacak bir yüzeye yetki verilmiş olurdu.
 *
 * Kişisel çalışma alanı sistem ROLÜ üretmiyor (CLOS-82 `[025]`) ama yetkisiz değil
 * (CLOS-636): kullanılabilir küme `INDIVIDUAL_KEYS` + sözleşme domaini, ve o yetkiler
 * role değil SAHİPLİĞE bağlanıyor (`hasAllPermissions`). Varsayılan rol ataması yine yok.
 */
/**
 * SPONSORLU MÜVEKKİL ALANINDA kullanılabilen anahtarlar (CLOS-176 `[123]`).
 *
 * ⚠ Bu küme YENİ YETKİ YARATMIYOR — kartın bağımlılık sınırı bunu yasaklıyor. Yalnız
 * KATALOGDA ZATEN VAR OLAN üç anahtarın bu türde kullanılabileceğini söylüyor: kendi
 * alanını görmek, adını düzenlemek ve erişebildiği sözleşmeleri okumak. Hukuk bürosunun iç
 * yüzeyi (departman, üyelik, rol yönetimi, müvekkil domaini) bu türde HİÇ yok — kart:
 * *"departman, tam organizasyon yönetimi, hukuk bürosu iç rolleri ... BULUNMAZ."*
 *
 * Kimin bu anahtarları taşıyacağı (temsilci rolü ve kapsamı) CLOS-178'in işi; burada
 * yalnız türün TAVANI çiziliyor.
 */
/**
 * KİŞİSEL ÇALIŞMA ALANINDA kullanılabilen anahtarlar (CLOS-636).
 *
 * ⚠ Buranın boş kalması ÖLÇÜLMÜŞ bir kusurdu: kişisel alanda menüde yalnız "Ana Sayfa"
 * görünüyor, sözleşme oluşturulamıyordu — oysa `config/packages.json` içindeki
 * `individual_free` paketi 5 sözleşme + 256 MiB hakkı SATIYOR ve CLOS-440 "Individual
 * paketini sözleşme hakkıyla modelle" diyor. Yani hak tanımlıydı, kullanılacak yüzey yoktu.
 *
 * Boş bırakılmasının gerekçesi kodda "kişisel alan sistem rolü üretmiyor (CLOS-82),
 * dolayısıyla varsayılan atama da almıyor" diye yazılıydı. O çıkarım kartta yok: CLOS-82
 * yalnız *Enterprise sistem rolü* üretilmediğini söylüyor, yetki olmadığını değil. Rol
 * üretmeden de yetki verilebilir — kişisel alanda erişim üyelikten değil SAHİPLİKTEN
 * geliyor (CLOS-636: "membership missing kişisel alan için geçerli hata değildir").
 *
 * Kapsam kartla sınırlı: sözleşme domaininin tamamı, bir de kendi alanını görmek ve
 * adlandırmak. Müvekkil, şablon, AI, üyelik/rol/departman ve abonelik yönetimi BURADA YOK
 * — kart onları istemiyor ve kartsız yüzey açılmaz (ADR-0014).
 */
/**
 * ŞABLON ANAHTARLARI kişisel alanda AÇIK (CLOS-825).
 *
 * ⚠ Şablon bir EKİP aracı değil, sözleşme ÜRETİM aracı. Kişisel alanda sözleşme domaininin
 * tamamı açıkken şablondan üretmenin kapalı olması tutarsızdı: kullanıcı boş sözleşme ve
 * dosyadan sözleşme üretebiliyor ama kendi yazdığı şablonu kullanamıyordu.
 *
 * ⚠ `archive` ve `manage` DIŞARIDA. İkisi de kütüphane yönetimi kavramları — arşivleme
 * başkalarının kullandığı bir şablonu gündemden çıkarmak, yönetme ise kütüphane
 * politikasını belirlemek. Kişisel alanda ne başkası ne de politika var; kartın istediği
 * asgari küme "görebilir, oluşturabilir, düzenleyebilir, kullanabilir".
 */
const INDIVIDUAL_TEMPLATE_KEYS: readonly string[] = [
  "template.view",
  "template.create",
  "template.edit",
  "template.use",
];

/**
 * ABONELİK OKUMA kişisel alanda AÇIK (CLOS-197 `[173]`).
 *
 * ### ⚠ VERİ ZATEN VARDI, KAPISI YOKTU
 *
 * Kişisel alan `individual_free` paketiyle abonelik satırı açıyor (CLOS-636) ve kapasite
 * sayaçları işliyor. Ama `subscription.*` anahtarlarının hiçbiri bu türün tavanında
 * değildi: kullanıcının verisi vardı, göreceği kapı yoktu. CLOS-197 bunu açıkça istiyor —
 * *"Individual kullanıcı yalnız kişisel çalışma alanına ait ticari ve kullanım bilgisini
 * görür."*
 *
 * ⚠ Bu bir "varsayımla ürün değişikliği" DEĞİL, tersi: kartın yazılı kriteri kanon,
 * çelişen taraf katalogdu ve uygulama kanona yetişiyor. Kapatmayı seçseydik kartın
 * kriterini varsayımla İPTAL etmiş olurduk. CLOS-636 emsali birebir aynı: orada da
 * sözleşme yüzeyi aynı mantıkla individual'a açılmıştı.
 *
 * ### ⚠ `payment`, `invoice`, `entitlement` CLOS-201/202 İLE AÇILDI — `plan`, `usage` HÂLÂ DIŞARIDA
 *
 * `13` sürümüne kadar bu küme yalnız `view` idi çünkü fatura profili, ödeme yöntemi ve ek
 * hak satın alma akışlarının kaynağı yoktu. CLOS-202 (fatura profili, ödeme yöntemi,
 * fatura belgeleri) ve CLOS-201 (ek hak satın alma) kişisel çalışma alanı için de gerçek
 * bir akış tanımlıyor — kartın kelimesi kelimesine kriteri: *"Individual finansal profil
 * yalnız kişisel çalışma alanına ... aittir"*. Kapalı bırakmak, VERİSİ olan bir alanın
 * KAPISI olmaması demekti; `subscription.view`'ün kendisinin doğduğu gerekçenin (CLOS-197)
 * aynısı.
 *
 * `plan` ve `usage` BİLEREK DIŞARIDA kalıyor: plan değiştirme CLOS-254/255'in, kullanım
 * sayaç ekranları CLOS-198–200'ün konusu ve bu iki kart onları açmadı. Okuma ile TÜM
 * yönetimi aynı anahtara bağlasaydık, bir ekranı açmak için verilen yetki sessizce paket
 * değiştirme yetkisi de olurdu — payment/invoice/entitlement'ın SINIRI kendi kartlarının
 * kapsamı (ödeme yürütme, fatura, ek hak satın alma), plan SEÇİMİ değil.
 *
 * ⚠ `subscription.*` hepsi `protected` sınıfta ve bu kişisel alanda risk DOĞURMUYOR: alan
 * tek sahiplidir, rol yoktur, paylaşılamaz ve sahip zaten türün kullanılabilir kümesinin
 * tamamını alır (`lib/permissions.ts` sahiplik yolu). Korunan sınıfın koruduğu şey
 * "bir çalışanın verisini bir başkasına açmak" — burada başkası yok.
 */
const INDIVIDUAL_SUBSCRIPTION_KEYS: readonly string[] = [
  "subscription.view",
  "subscription.payment",
  "subscription.invoice",
  "subscription.entitlement",
];

const INDIVIDUAL_KEYS: readonly string[] = [
  "organization.view",
  "organization.profile",
  ...INDIVIDUAL_TEMPLATE_KEYS,
  ...INDIVIDUAL_SUBSCRIPTION_KEYS,
];

/**
 * BAĞIMSIZ AVUKATIN müvekkil kümesi (CLOS-813).
 *
 * ⚠ ASGARİ ve BİLEREK dar. CLOS-810 kapsamı sayıyor: *"Client create, list/search/filter,
 * detail/update, archive/reactivate ... Enterprise-only iç ekip/departman/portföy
 * sorumlulukları kişisel alanda açılmaz."*
 *
 * Dışarıda kalanlar ve sebepleri:
 * — `client.portfolio` büro İÇİ sorumluluk ataması; kişisel alanda atanacak ekip yok.
 *   (Bugün sözleşme bağlama ucu da bu anahtara bakıyor; ayrıştırması CLOS-819'un işi.)
 * — `client.workspace` sponsorlu alan kurma; hukuk bürosunun LİSANS hakkı.
 * — `client.service_scope` hizmet ilişkisi kapsamı; iki organizasyon arasındaki kavram.
 * — `client.representative` temsilci yönetimi; CLOS-820 kendi kartında karar veriyor.
 * — `client.merge` geri alınamaz ve kartın asgari listesinde yok.
 */
const INDEPENDENT_LAWYER_CLIENT_KEYS: readonly string[] = [
  "client.view",
  "client.create",
  "client.edit",
  "client.archive",
  /** Sözleşme bağı CLOS-819 ile kendi anahtarına ayrıldı; kartın asgari listesinde var. */
  "client.contract_link",
];

const CLIENT_WORKSPACE_KEYS: readonly string[] = [
  "organization.view",
  "organization.profile",
  "contract.view",
  /**
   * Hizmet ilişkisi kararları (CLOS-246 `[133]`) — [123]'ün "üç anahtar" tavanı BİLEREK
   * dörde çıkıyor: müvekkil tarafının onayı bu türün yüzeyinde veriliyor ve kartsız
   * yüzey açılmadığı gibi kapısız da açılmıyor. İnce ayrım (temsilcinin `service_actions`
   * kapsamı) rolsüz tavanda İFADE EDİLEMEZ; o kapı serviste (CLOS-178 adapter'ı).
   */
  "organization.service",
  /**
   * DEĞİŞİKLİK ÖNERİSİ (Müvekkil Alanı remediation · Halit onayı, 6 Eylül 2026).
   *
   * ⚠ TAVAN, GRANT DEĞİL. Bu liste `client_workspace` türündeki bir alanın taşıyabileceği
   * EN GENİŞ kümedir; kimsenin eline otomatik izin vermez. Öneri gönderebilmek için üç
   * katmanın üçü de gerekiyor: bu tavan ∩ sözleşme katılım tavanı ∩ temsilcinin fiilî izni.
   *
   * ⚠ Neden gerekiyordu: tavanda olmayan bir anahtar rol/üyelik yoluyla HİÇ verilemiyor,
   * yani `client_workspace` kullanıcısı `contract.revision` iznini yapısal olarak
   * alamıyordu ve `requirePermission("contract.revision")` asla geçmiyordu. Ürün kararı
   * müvekkil temsilcisinin değişiklik önerebilmesini istiyor; kapı buradan açılıyor.
   *
   * ⚠ `view`'dan TÜRETİLMİYOR: görüntüleme ile öneri ayrı yeteneklerdir ve onay, mutabakat,
   * imza, indirme hâlâ tavanın dışında.
   */
  "contract.revision",
];

/**
 * Bir çalışma alanı türünün (ve kişiselse arketipinin) TAVANI.
 *
 * ⚠ `usageArchetype` İSTEĞE BAĞLI ve varsayılanı `self` — yani çağıran unutursa küme
 * GENİŞLEMİYOR, daralıyor. Ters tasarlansaydı arketipi geçirmeyi unutan bir kod yolu
 * bağımsız avukat yetkilerini herkese açardı; fail-closed yön budur.
 *
 * ⚠ Arketip yalnız `individual` türünde okunuyor. Kurumsal alanda "ne tür" sorusunu `type`
 * cevaplıyor ve iki kavram birbirinin yerine geçmez (CLOS-807).
 */
export function permissionsForTenantType(
  type: TenantType,
  usageArchetype: UsageArchetype = "self",
): PermissionEntry[] {
  if (type === "enterprise_law_firm") return [...PERMISSION_CATALOG];
  if (type === "enterprise_company") {
    return PERMISSION_CATALOG.filter((p) => p.domain !== "client");
  }
  if (type === "client_workspace") {
    return PERMISSION_CATALOG.filter((p) => CLIENT_WORKSPACE_KEYS.includes(p.key));
  }
  if (type === "individual") {
    const arketipAnahtarlari =
      usageArchetype === "independent_lawyer" ? INDEPENDENT_LAWYER_CLIENT_KEYS : [];
    return PERMISSION_CATALOG.filter(
      (p) =>
        p.domain === "contract" ||
        INDIVIDUAL_KEYS.includes(p.key) ||
        arketipAnahtarlari.includes(p.key),
    );
  }
  return [];
}

/**
 * Sistem rolünün VARSAYILAN permission seti.
 *
 * Kural risk sınıfından TÜRETİLİYOR, elle yazılmış bir tablodan değil:
 *
 * - **Organizasyon Yöneticisi** — türde kullanılabilir her permission. Korunan sınıfın
 *   *"yalnız kanonik yetkili roller"* sahibi bu rol (kart: abonelik korunan sınıftadır).
 * - **Standart Kullanıcı** — yalnız `standard` sınıfı. Hassas işlemler (imza, onay,
 *   paylaşım, arşiv, üyelik) geri alınamaz ya da organizasyon dışına çıkan sonuçlar
 *   doğuruyor; varsayılan olarak herkese vermek, yetkiyi varsayılan olarak açmak demekti.
 *
 * Elle tablo yazılsaydı yeni bir permission eklendiğinde iki listeyi de güncellemek
 * gerekir ve biri unutulurdu — unutulan taraf çoğu zaman kısıtlayıcı olan olur, yani
 * yetki sessizce genişlerdi.
 */
export function defaultPermissionsFor(role: SystemRoleKey, type: TenantType): string[] {
  /**
   * ⚠ KİŞİSEL ALANDA ROL YOK, dolayısıyla rol başına varsayılan atama da yok
   * (CLOS-82 `[025]`, CLOS-394: "ayrı bir Owner rolü veya Owner üyelik tipi oluşturulmaz").
   *
   * Türün kullanılabilir kümesi dolu (CLOS-636) ama o yetkiler role değil SAHİPLİĞE
   * bağlanıyor. Bu ayrım olmasaydı kişisel alan için hiç var olmayacak rollere satır
   * yazardık — atanmayı bekleyen ölü kayıtlar.
   */
  if (type === "individual") return [];

  const kullanilabilir = permissionsForTenantType(type);
  /**
   * ⚠ SYSTEM GOVERNANCE CORE BURADA VERİLMİYOR (CLOS-1209 — kilitli karar).
   *
   * Çekirdek, designation'dan TÜREYEN bir effective capability katmanıdır; role fiziksel
   * satır olarak yazılmaz. İlk uygulamada yazılıyordu ve canlı turda kusuru ortaya çıktı:
   * yönetişimi devreden rol `organization.governance` satırını üzerinde tutmaya devam
   * ediyor, eski aktör devri tek taraflı geri alabiliyordu — designation "tek authoritative
   * kaynak" olmaktan çıkmıştı.
   *
   * Satır olarak yazsaydık ayrıca rol klonlama, kalıtım ve izin kopyalama çekirdeği
   * taşıyabilirdi (supersede S06'nın kapattığı kanal).
   */
  if (role === "org_admin") {
    return kullanilabilir.map((p) => p.key).filter((k) => !isNeverStored(k));
  }
  return kullanilabilir.filter((p) => p.risk === "standard").map((p) => p.key);
}
